/* eslint-disable array-callback-return */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Card,
  Chip,
  InputAdornment,
  Input,
  TablePagination,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import Header from '../../components/Header';
import { useTranslation } from '../../hooks/useTranslation';
import { useCookiesLogin } from '../../config/cookies';
import { formatDateBR } from '../../utils';
import { useNavigate } from 'react-router';
import api from '../../services/api';

const STAGES = {
  APPROVED: 'Aprovado',
  PENDING: 'Pendente',
  REJECTED: 'Reprovado',
};

const Redrural = () => {
  const { cookies } = useCookiesLogin();
  const { last_company: bussines_id } = cookies?.AUTH_TOKEN || {};

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [simulations, setSimulations] = useState([]);
  const [filteredSimulations, setFilteredSimulations] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    revenda: 0,
    redutoCapital: 0,
    pendentes: 0,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const paginatedSimulations = (filteredSimulations ?? []).slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const { t } = useTranslation();
  const navigate = useNavigate();

  const fetchSimulations = useCallback(
    async (term = '') => {
      try {
        setLoading(true);

        const cleanTerm = term.trim();
        const { data: resultado } = await api.get(`/v1/simulation?search=${cleanTerm}`);
        
        setSimulations(resultado.list ?? []);
        setFilteredSimulations(resultado.list ?? []);
        setStats({
          total: resultado.overview.total,
          revenda: resultado.overview.revenda,
          redutoCapital: resultado.overview.reduto_capital,
          pendentes: resultado.overview.pending,
        });
      } catch (error) {
        console.error(t('messages.errorFetchingData'), error);
      } finally {
        setLoading(false);
      }
    },
    [bussines_id]
  );

  useEffect(() => {
    fetchSimulations();
  }, [fetchSimulations]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleSearch = () => {
    fetchSimulations(searchTerm);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  //Para futuras telas
  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return { bg: '#DCFCE7', color: '#16A34A' };
      case 'PENDING':
        return { bg: '#FEF9C3', color: '#CA8A04' };
      case 'REJECTED':
        return { bg: '#FDE8E8', color: '#DC2626' };
      default:
        return { bg: '#F3F4F6', color: '#6B7280' };
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Typography variant="h6" sx={{ color: '#757575' }}>
          {t('common.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, minHeight: '100vh' }}>
          {/* Header */}
          <Header
            title={t('titles.customerManagement')}
            subtitle={t('subTitles.manageRegistrations')}
          />

          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={6} md={3}>
              <Card
                sx={{
                  bgcolor: '#FFFFFF',
                  boxShadow: 1,
                  borderRadius: '12px',
                  border: '1px solid #E1E7EF',
                  padding: '16px',
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: '#757575', mb: 0.5, fontSize: { xs: '14px', md: '16px' } }}
                  >
                    Total
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ color: '#2C2C2C', fontWeight: 700, fontSize: '24px' }}
                  >
                    {stats.total}
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Card
                sx={{
                  bgcolor: '#E7F0FE',
                  boxShadow: 1,
                  borderRadius: '12px',
                  border: '1px solid #3C83F633',
                  padding: '16px',
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: '#3C83F6', mb: 0.5, fontSize: { xs: '14px', md: '16px' } }}
                  >
                    {t('simulation.resale')}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ color: '#3C83F6', fontWeight: 700, fontSize: '24px' }}
                  >
                    {stats.revenda}
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Card
                sx={{
                  bgcolor: '#EDFFE2',
                  boxShadow: 1,
                  borderRadius: '12px',
                  border: '1px solid #83D84D',
                  padding: '16px',
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: '#166534', mb: 0.5, fontSize: { xs: '14px', md: '16px' } }}
                  >
                    Reduto Capital
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ color: '#166534', fontWeight: 700, fontSize: '24px' }}
                  >
                    {stats.redutoCapital}
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Card
                sx={{
                  bgcolor: '#FEF8E6',
                  boxShadow: 1,
                  borderRadius: '12px',
                  border: '1px solid #854D0E33',
                  padding: '16px',
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: '#854D0E', mb: 0.5, fontSize: { xs: '14px', md: '16px' } }}
                  >
                    {t('simulation.pending')}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ color: '#E7B008', fontWeight: 700, fontSize: '24px' }}
                  >
                    {stats.pendentes}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Search and Actions */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              gap: 2,
              mb: 3,
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: '24px',
              }}
            >
              <Input
                disableUnderline
                placeholder={t('simulation.searchByname')}
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchTerm(value);
                  if (value === '') fetchSimulations();
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                sx={{
                  flex: 1,
                  px: { xs: 1.5, md: 3 },
                  py: 1.2,
                  width: { xs: '100%', md: '380px' },
                  borderRadius: '8px',
                  bgcolor: '#FFFFFF',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                    },
                  },
                  '& input::placeholder': {
                    fontSize: { xs: '12px', md: '14px' },
                    color: '#9CA3AF',
                    opacity: 1,
                  },
                }}
                startAdornment={
                  <InputAdornment
                    position="start"
                    sx={{
                      mr: { xs: 0.5, md: 1 },
                    }}
                  >
                    <SearchIcon sx={{ color: '#9CA3AF', fontSize: { xs: '16px', md: '20px' } }} />
                  </InputAdornment>
                }
              />
              <Button
                variant="outlined"
                onClick={handleClearSearch}
                sx={{
                  bgcolor: '#83D84D',
                  color: '#2C2C2C',
                  borderColor: '#83D84D',
                  border: 'none',
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#73C43D',
                    boxShadow: 'none',
                    border: 'none',
                  },
                }}
              >
                {t('simulation.clear').toUpperCase()}
              </Button>
            </Box>
            <Button
              onClick={() => navigate('/app/simulation')}
              variant="contained"
              sx={{
                bgcolor: '#83D84D',
                color: '#2C2C2C',
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#73C43D',
                  boxShadow: 'none',
                },
              }}
            >
              {t('simulation.createNewSimulation').toUpperCase()}
            </Button>
          </Box>

          {/* Table */}
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 1,
              borderRadius: 2,
              overflowX: 'auto',
            }}
          >
            <Table sx={{ minWidth: { xs: 300, sm: 650 } }}>
              <TableHead sx={{ bgcolor: '#FAFAFA' }}>
                <TableRow>
                  <TableCell sx={{ color: '#2C2C2C', fontWeight: 600, fontSize: '16px' }}>
                    {t('simulation.name')}
                  </TableCell>
                  <TableCell sx={{ color: '#2C2C2C', fontWeight: 600, fontSize: '16px' }}>
                    {t('simulation.value')}
                  </TableCell>
                  <TableCell sx={{ color: '#2C2C2C', fontWeight: 600, fontSize: '16px' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ color: '#2C2C2C', fontWeight: 600, fontSize: '16px' }}>
                    {t('simulation.date')}
                  </TableCell>
                  <TableCell sx={{ width: 50 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedSimulations.map((client) => (
                  <TableRow
                    key={client.id}
                    sx={{
                      '&:hover': {
                        bgcolor: '#FAFAFA',
                      },
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: '#2C2C2C', fontWeight: 600, mb: 0.5, fontSize: '14px' }}
                        >
                          {client.client_name}
                        </Typography>
                        {client?.company && (
                          <Typography variant="caption" sx={{ color: '#757575', fontSize: '12px' }}>
                            {client?.company?.name}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#737373', fontSize: '14px' }}>
                      {formatCurrency(client.total_value)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STAGES[client.stage]}
                        size="small"
                        icon={
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: getStatusColor(client.stage).color,
                            }}
                          />
                        }
                        sx={{
                          bgcolor: getStatusColor(client.stage).bg,
                          color: getStatusColor(client.stage).color,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          height: 24,
                          '& .MuiChip-label': {
                            px: 1.5,
                          },
                          '& .MuiChip-icon': {
                            ml: 0.75,
                            mr: -0.75,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#737373', fontSize: '14px' }}>
                      {formatDateBR(client.created_at)}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => navigate(`/app/simulation/${client?.id}`)}
                        sx={{
                          bgcolor: '#fafafa',
                          width: 36,
                          height: 36,
                          '&:hover': {
                            bgcolor: '#73C43D',
                          },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18, color: '#00000' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={simulations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />

          {/* Empty State */}
          {filteredSimulations.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                bgcolor: '#FFFFFF',
                borderRadius: 2,
                mt: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: '#757575', mb: 1, fontSize: '16px' }}>
                {t('simulation.noCustomersFound')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '14px' }}>
                {t('simulation.tryAdjustingSearch')}
              </Typography>
            </Box>
          )}
        </Box>
    </>
  );
};

export default Redrural;
